import { useEffect, useState } from "react"
import { useTimeMachine } from "../../contexts/TimeMachineContext"
import { YoungWithAttendanceAndMetadata } from "../../pages/admin/FaireLAppel"
import {
   AttendanceTime,
   attendanceTimeToDate,
   attendanceTimeTranslations,
} from "../../types/AttendanceTime"
import { useGlobalSettings } from "@/entities/globalSettings/useGlobalSettings"
import { doesYoungHaveOverrides } from "@/controller/handleFaireLAppelSubmit"

interface AttendanceContextDrawerProps {
   selectedYoung: YoungWithAttendanceAndMetadata | null
   onClose: () => void
   bounce?: boolean
   onChange: (params: { youngId: string; data: Partial<YoungWithAttendanceAndMetadata> }) => void
}

export function AttendanceContextDrawer({
   onClose,
   bounce,
   selectedYoung,
   onChange,
}: AttendanceContextDrawerProps) {
   const { now } = useTimeMachine()
   const globalSettingsQuery = useGlobalSettings()
   const [shouldBounce] = useState(false)
   const [presentFromType, setPresentFromType] = useState<AttendanceTime>(
      selectedYoung?.presentFromType || "start_of_day",
   )
   const [presentFromTime, setPresentFromTime] = useState(selectedYoung?.presentFromTime || "09:00")

   const [lateFromType, setLateFromType] = useState<AttendanceTime>(
      selectedYoung?.lateFromType || "start_of_day",
   )
   const [lateFromTime, setLateFromTime] = useState(selectedYoung?.lateFromTime || "09:00")

   const [absenceFromType, setAbsenceFromType] = useState<AttendanceTime>(
      selectedYoung?.absenceFromType || "start_of_day",
   )
   const [absenceFromTime, setAbsenceFromTime] = useState(selectedYoung?.absenceFromTime || "09:00")
   const [absenceToType, setAbsenceToType] = useState<AttendanceTime>(
      selectedYoung?.absenceToType || "unknown",
   )
   const [absenceToTime, setAbsenceToTime] = useState(selectedYoung?.absenceToTime || "17:00")

   const filterAttendanceTimeOptions = (
      options: AttendanceTime[],
      operator: "before" | "after",
      now: Date,
   ): AttendanceTime[] => {
      return options.filter((option) => {
         if (option === "now" || option === "specific_time") return true
         if (!globalSettingsQuery.data) return false

         const optionDate = attendanceTimeToDate(globalSettingsQuery.data, option, now)
         return operator === "before" ? optionDate <= now : optionDate >= now
      })
   }

   const attendanceTimeOptions = {
      present: filterAttendanceTimeOptions(
         [
            "now",
            "start_of_day",
            "morning_break",
            "lunch_break",
            "afternoon_break",
            "end_of_day",
            "specific_time",
         ] as AttendanceTime[],
         "before",
         now,
      ),
      late: filterAttendanceTimeOptions(
         [
            "now",
            "start_of_day",
            "morning_break",
            "lunch_break",
            "afternoon_break",
            "end_of_day",
            "specific_time",
         ] as AttendanceTime[],
         "before",
         now,
      ),
      absentFrom: [
         "now",
         "start_of_day",
         "morning_break",
         "lunch_break",
         "afternoon_break",
         "end_of_day",
         "specific_time",
      ] as AttendanceTime[],
      absentTo: [
         "unknown",
         "now",
         "start_of_day",
         "morning_break",
         "lunch_break",
         "afternoon_break",
         "end_of_day",
         "specific_time",
      ] as AttendanceTime[],
   }

   useEffect(() => {
      if (selectedYoung) {
         setPresentFromType(selectedYoung.presentFromType)
         setPresentFromTime(selectedYoung.presentFromTime)
         setLateFromType(selectedYoung.lateFromType)
         setLateFromTime(selectedYoung.lateFromTime)
         setAbsenceFromType(selectedYoung.absenceFromType)
         setAbsenceFromTime(selectedYoung.absenceFromTime)
         setAbsenceToType(selectedYoung.absenceToType)
         setAbsenceToTime(selectedYoung.absenceToTime)
      }
   }, [selectedYoung])

   if (selectedYoung === null) return null

   const youngHasOverride = doesYoungHaveOverrides(selectedYoung)

   const handlePresentFromTypeChange = (value: AttendanceTime) => {
      setPresentFromType(value)
      if (selectedYoung) {
         onChange({
            youngId: selectedYoung.id,
            data: { presentFromType: value },
         })
         if (value !== "specific_time") {
            onClose()
         }
      }
   }

   const handlePresentFromTimeChange = (value: string) => {
      setPresentFromTime(value)
      if (selectedYoung) {
         onChange({
            youngId: selectedYoung.id,
            data: { presentFromTime: value },
         })
         if (presentFromType !== "specific_time") {
            onClose()
         }
      }
   }

   const handleLateFromTypeChange = (value: AttendanceTime) => {
      setLateFromType(value)
      if (selectedYoung) {
         onChange({
            youngId: selectedYoung.id,
            data: { lateFromType: value },
         })
         if (value !== "specific_time") {
            onClose()
         }
      }
   }

   const handleLateFromTimeChange = (value: string) => {
      setLateFromTime(value)
      if (selectedYoung) {
         onChange({
            youngId: selectedYoung.id,
            data: { lateFromTime: value },
         })
         if (lateFromType !== "specific_time") {
            onClose()
         }
      }
   }


   const handleAbsenceFromTypeChange = (value: AttendanceTime) => {
      setAbsenceFromType(value)
      if (selectedYoung) {
         onChange({
            youngId: selectedYoung.id,
            data: { absenceFromType: value },
         })
      }
   }

   const handleAbsenceFromTimeChange = (value: string) => {
      setAbsenceFromTime(value)
      if (selectedYoung) {
         onChange({
            youngId: selectedYoung.id,
            data: { absenceFromTime: value },
         })
      }
   }

   const handleAbsenceToTypeChange = (value: AttendanceTime) => {
      setAbsenceToType(value)
      if (selectedYoung) {
         onChange({
            youngId: selectedYoung.id,
            data: { absenceToType: value },
         })
         if (value !== "specific_time") {
            onClose()
         }
      }
   }

   const handleAbsenceToTimeChange = (value: string) => {
      setAbsenceToTime(value)
      if (selectedYoung) {
         onChange({
            youngId: selectedYoung.id,
            data: { absenceToTime: value },
         })
         if (absenceToType !== "specific_time") {
            onClose()
         }
      }
   }

   return (
      <>
         <div style={{ marginTop: "12rem" }}></div>
         <div
            className={`fixed bottom-0 left-0 right-0 min-h-[5rem] bg-base-100 border-t border-base-300 transition-transform duration-100 ease-in-out transform ${
               selectedYoung ? "translate-y-0" : "translate-y-full"
            } ${bounce || shouldBounce ? " " : ""}`}
         >
            <div className="container mx-auto p-4 relative">
               <button
                  onClick={onClose}
                  className="btn btn-circle btn-ghost absolute top-2 right-2"
               >
                  <svg
                     xmlns="http://www.w3.org/2000/svg"
                     fill="none"
                     viewBox="0 0 24 24"
                     strokeWidth={1.5}
                     stroke="currentColor"
                     className="size-6"
                  >
                     <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                  </svg>
               </button>

               {selectedYoung && (
                  <div className="flex flex-col gap-4">
                     {import.meta.env.VITE_DEV_SHOW_IDS === "true" && <p> {selectedYoung.id} </p>}

                     {/* young Has  *NO* Override ------------------------------------- */}
                     {!youngHasOverride && (
                        <>
                           <div className="flex flex-col gap-4">
                              <h3 className="text-lg font-bold">
                                 {selectedYoung.firstName} {selectedYoung.lastName}{" "}
                                 {selectedYoung.current_attendance_status === "present" &&
                                    "est actuellement là"}
                                 {selectedYoung.current_attendance_status === "late" && (
                                    <>
                                       a actuellement un retard{" "}
                                       {selectedYoung.is_current_attendance_expected && "prévu"}
                                    </>
                                 )}
                                 {selectedYoung.current_attendance_status === "absent" && (
                                    <>
                                       a actuellement une absence{" "}
                                       {selectedYoung.is_current_attendance_expected && "prévue"}
                                    </>
                                 )}
                              </h3>
                           </div>
                        </>
                     )}

                     {/* young Has Override ------------------------------------- */}
                     {youngHasOverride && (
                        <>
                           {selectedYoung.newStatus === "present" ? (
                              <div className="flex flex-col gap-4">
                                 <h3 className="text-lg font-bold">
                                    {selectedYoung.firstName} {selectedYoung.lastName}
                                    {selectedYoung.current_attendance_status === "present"
                                       ? " est là"
                                       : " est de retour"}
                                 </h3>
                                 {selectedYoung.current_attendance_status !== "present" && (
                                    <div className="form-control">
                                       <div className="flex flex-col gap-2">
                                          <div className="flex items-center gap-2">
                                             <div className="label min-w-16">
                                                <span className="label-text">Depuis</span>
                                             </div>
                                             <select
                                                className="select select-bordered flex-1"
                                                value={presentFromType}
                                                onChange={(e) =>
                                                   handlePresentFromTypeChange(
                                                      e.target.value as AttendanceTime,
                                                   )
                                                }
                                             >
                                                {attendanceTimeOptions.present.map((option) => (
                                                   <option key={option} value={option}>
                                                      {attendanceTimeTranslations[option]}
                                                   </option>
                                                ))}
                                             </select>
                                          </div>
                                          {presentFromType === "specific_time" && (
                                             <>
                                             <div className="flex items-center gap-2">
                                                <div className="label-text min-w-16">&nbsp;</div>
                                                <input
                                                   type="time"
                                                   className="input input-bordered w-full"
                                                   value={presentFromTime}
                                                   onChange={(e) =>
                                                      handlePresentFromTimeChange(e.target.value)
                                                   }
                                                   step="900"
                                                />
                                             </div>
                                             </>
                                          )}
                                       </div>
                                    </div>
                                 )}
                              </div>
                           ) : selectedYoung.newStatus === "late" ? (
                              <>
                                 <h3 className="text-lg font-bold">
                                    {selectedYoung.firstName} {selectedYoung.lastName} a un
                                    <span
                                       className={`  ${
                                          selectedYoung.newExpected
                                             ? "text-green-600"
                                             : "text-yellow-600"
                                       }`}
                                    >
                                       {" "}
                                       retard {selectedYoung.newExpected && "prévu"}
                                    </span>
                                 </h3>
                                 <div className="form-control">
                                       <div className="flex flex-col gap-2">
                                          <div className="flex items-center gap-2">
                                             <div className="label min-w-16">
                                                <span className="label-text">Depuis</span>
                                             </div>
                                             <select
                                                className="select select-bordered flex-1"
                                                value={lateFromType}
                                                onChange={(e) =>
                                                   handleLateFromTypeChange(
                                                      e.target.value as AttendanceTime,
                                                   )
                                                }
                                             >
                                                {attendanceTimeOptions.late.map((option) => (
                                                   <option key={option} value={option}>
                                                      {attendanceTimeTranslations[option]}
                                                   </option>
                                                ))}
                                             </select>
                                          </div>
                                          {lateFromType === "specific_time" && (
                                             <>
                                             <div className="flex items-center gap-2">
                                                <div className="label-text min-w-16">&nbsp;</div>
                                                <input
                                                   type="time"
                                                   className="input input-bordered w-full"
                                                   value={lateFromTime}
                                                   onChange={(e) =>
                                                      handleLateFromTimeChange(e.target.value)
                                                   }
                                                   step="900"
                                                />
                                             </div>
                                             </>
                                          )}
                                       </div>
                                    </div>
                              </>
                           ) : selectedYoung.newStatus === "absent" ? (
                              <div className="flex flex-col gap-4">
                                 <h3 className="text-lg font-bold">
                                    {selectedYoung.firstName} {selectedYoung.lastName} a une
                                    <span
                                       className={`  ${
                                          selectedYoung.newExpected
                                             ? "text-green-600"
                                             : "text-red-600"
                                       }`}
                                    >
                                       {" "}
                                       absence {selectedYoung.newExpected && "prévue"}
                                    </span>
                                 </h3>

                                 <div className="form-control">
                                    <div className="flex flex-col gap-2">
                                       <div className="flex items-center gap-2">
                                          <div className="label min-w-16">
                                             <span className="label-text">Depuis</span>
                                          </div>
                                          <select
                                             className="select select-bordered flex-1"
                                             value={absenceFromType}
                                             onChange={(e) =>
                                                handleAbsenceFromTypeChange(
                                                   e.target.value as AttendanceTime,
                                                )
                                             }
                                          >
                                             {attendanceTimeOptions.absentFrom.map((option) => (
                                                <option key={option} value={option}>
                                                   {attendanceTimeTranslations[option]}
                                                </option>
                                             ))}
                                          </select>
                                       </div>
                                       {absenceFromType === "specific_time" && (
                                          <>
                                          <div className="flex items-center gap-2">
                                             <div className="label-text min-w-16">&nbsp;</div>
                                             <input
                                                type="time"
                                                className="input input-bordered w-full"
                                                value={absenceFromTime}
                                                onChange={(e) =>
                                                   handleAbsenceFromTimeChange(e.target.value)
                                                }
                                                step="900"
                                             />
                                          </div>
                                          </>
                                       )}
                                    </div>
                                 </div>

                                 <div className="form-control">
                                    <div className="flex flex-col gap-2">
                                       <div className="flex items-center gap-2">
                                          <div className="label min-w-16">
                                             <span className="label-text">Jusqu'à</span>
                                          </div>
                                          <select
                                             className="select select-bordered flex-1"
                                             value={absenceToType}
                                             onChange={(e) =>
                                                handleAbsenceToTypeChange(
                                                   e.target.value as AttendanceTime,
                                                )
                                             }
                                          >
                                             {attendanceTimeOptions.absentTo.map((option) => (
                                                <option key={option} value={option}>
                                                   {attendanceTimeTranslations[option]}
                                                </option>
                                             ))}
                                          </select>
                                       </div>
                                       {absenceToType === "specific_time" && (
                                          <div className="flex items-center gap-2">
                                             <div className="label-text min-w-16">&nbsp;</div>
                                             <input
                                                type="time"
                                                className="input input-bordered w-full"
                                                value={absenceToTime}
                                                onChange={(e) =>
                                                   handleAbsenceToTimeChange(e.target.value)
                                                }
                                                step="900"
                                             />
                                          </div>
                                       )}
                                    </div>
                                 </div>
                              </div>
                           ) : null}
                        </>
                     )}
                  </div>
               )}
            </div>
         </div>
      </>
   )
}
